// ----------------------------------------------------------------------------
// useChordLibrary — data + upload orchestration for the Chord AI library.
// Keeps the page component thin: handles upload (SAS PUT -> org record ->
// analyze) and polls in-progress analyses, persisting results into the church DB.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { chordUploadsApi, chordsApi, sha256Hex, type ChordUpload } from '@app/api/chords';
import { notifyChord, useChordWatch } from '../context/ChordWatchProvider';
import { prepareAudioForUpload } from '../utils/chordAudio';
import { logger } from '@app/utils/logger';
import { trackEvent } from '@app/utils/telemetry';

/**
 * Convert an upload failure into a short, user-friendly message. Raw server
 * (5xx) and network errors never surface technical text like "Request failed
 * with status code 500"; safe 4xx messages from our own API (unsupported file
 * type, plan limits) are kept because they help the user. Client-side prep
 * errors (size / duration / decode) already carry friendly messages.
 */
function toFriendlyUploadError(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { error?: string; message?: string } };
    request?: unknown;
    message?: string;
  };

  if (e?.response) {
    const status = e.response.status ?? 0;
    const backendMsg = e.response.data?.error || e.response.data?.message;
    if (status === 401) return 'Your session expired. Please sign in again.';
    if (status === 402) return backendMsg || 'Your current plan does not include Song Studio uploads.';
    if (status === 409) return backendMsg || 'Your church has reached the limit of 20 Song Studio songs. Please replace an existing song.';
    if (status === 413) return 'That file is too large to upload. Try a shorter clip or compress it first.';
    if (status >= 400 && status < 500) {
      return backendMsg || 'We could not process that file. Please check it and try again.';
    }
    // 5xx: never expose the raw server error to the user.
    return 'Our chord service is temporarily unavailable. Please try again in a moment.';
  }

  // Request made but no response (network / CORS / timeout).
  if (e?.request) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  return e?.message || 'Upload failed. Please try again.';
}

// Outcome of an upload attempt, so callers can drive an upload indicator:
//   ok        - bytes reached our storage + analysis was queued (safe to leave)
//   duplicate - the same song was already in the library (nothing uploaded)
//   failed    - the upload did not finish
//   skipped   - no church context (nothing attempted)
export type UploadResult = 'ok' | 'duplicate' | 'failed' | 'skipped';

export function useChordLibrary(churchId: string | undefined) {
  const [uploads, setUploads] = useState<ChordUpload[]>([]);
  const [preparing, setPreparing] = useState<ChordUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { watch, lastCompleted } = useChordWatch();

  const reload = useCallback(async () => {
    if (!churchId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      setUploads(await chordUploadsApi.list(churchId));
    } catch (err) {
      logger.error('Failed to load chord uploads:', err);
      setUploads([]);
      setLoadError(toFriendlyUploadError(err));
    } finally {
      setLoading(false);
    }
  }, [churchId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const uploadSong = useCallback(
    async (file: File, title: string, keyHint?: string, replaceSourceId?: string, artist?: string): Promise<UploadResult> => {
      if (!churchId) return 'skipped';

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const cleanTitle = title.trim() || 'Untitled song';
      const hint = keyHint?.trim() || undefined;

      // Optimistic placeholder so the user sees it queued the instant the modal
      // closes (preparing + uploading can take a few seconds for large files).
      setPreparing((prev) => [
        { sourceId: tempId, title: cleanTitle, artist: artist?.trim() || undefined, status: 'pending', sourceType: 'upload' },
        ...prev,
      ]);

      // Upload funnel telemetry: one event per stage so App Insights shows
      // exactly where uploads die (prepare vs PUT vs register vs analyze).
      const t0 = performance.now();
      const fileMb = Math.round((file.size / (1024 * 1024)) * 10) / 10;
      const funnel = (stage: string, extra?: Record<string, unknown>) =>
        trackEvent('chord_upload', {
          stage,
          fileMb,
          fileType: file.type || 'unknown',
          elapsedMs: Math.round(performance.now() - t0),
          ...extra,
        });
      funnel('started');

      try {
        // If replacing an existing song: delete audio, database content, and stems first
        if (replaceSourceId) {
          funnel('replace-started', { replaceSourceId });
          try {
            await chordsApi.deleteSource(replaceSourceId);
          } catch (err) {
            logger.warn('Failed to delete chord storage assets during replace:', err);
          }
          try {
            await chordUploadsApi.remove(churchId, replaceSourceId);
          } catch (err) {
            logger.warn('Failed to delete chord record during replace:', err);
          }
          setUploads((prev) => prev.filter((u) => u.sourceId !== replaceSourceId));
        }

        // Fingerprint the selected file so the same song isn't uploaded twice.
        const contentHash = await sha256Hex(file);
        const dup = uploads.find((u) => u.contentHash && u.contentHash === contentHash && u.sourceId !== replaceSourceId);
        if (dup) {
          setPreparing((prev) => prev.filter((p) => p.sourceId !== tempId));
          notifyChord('Already in your library', `"${dup.title}" is already uploaded.`);
          funnel('duplicate-local');
          return 'duplicate';
        }

        // Client-side: enforce the 1 hour cap and shrink large files.
        const prepared = await prepareAudioForUpload(file);
        funnel('prepared', { converted: prepared !== file, preparedMb: Math.round((prepared.size / (1024 * 1024)) * 10) / 10 });

        // Get an upload target, then PUT the bytes (Azure SAS or local worker).
        // The server derives a content-addressed sourceId from the hash; when the
        // exact bytes already exist it returns duplicate=true so we skip the PUT.
        const sas = await chordsApi.getUploadUrl(prepared.name, contentHash);
        if (!sas.duplicate) {
          await chordsApi.uploadToBlob(sas.uploadUrl, prepared, sas.requiredHeaders);
        }
        funnel('uploaded', { sourceId: sas.sourceId, dedupedBlob: !!sas.duplicate });

        // Register the church-scoped record (returns the tier's includeStems).
        const record = await chordUploadsApi.create(churchId, {
          sourceId: sas.sourceId,
          blobName: sas.blobName,
          contentHash,
          title: cleanTitle,
          // The record's own optional field; omitted entirely when blank.
          ...(artist?.trim() ? { artist: artist.trim() } : {}),
          keyHint: hint,
          sourceType: 'upload',
          replaceSourceId,
        });

        // Kick off analysis (stems only when entitled).
        await chordsApi.analyze({
          uploadBlobName: sas.blobName,
          keyHint: hint,
          includeStems: record.includeStems,
        });
        funnel('queued', { sourceId: sas.sourceId });

        // Hand off to the global watcher so the user can navigate away and still
        // get notified when the analysis finishes.
        watch(sas.sourceId, cleanTitle);
        await reload();
        setPreparing((prev) => prev.filter((p) => p.sourceId !== tempId));
        return 'ok';
      } catch (err) {
        // Log the raw error for diagnostics, but show the user a friendly message.
        logger.error('Chord upload failed:', err);
        funnel('failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        const message = toFriendlyUploadError(err);
        setPreparing((prev) =>
          prev.map((p) => (p.sourceId === tempId ? { ...p, status: 'failed', error: message } : p)),
        );
        notifyChord('Upload failed', `"${cleanTitle}": ${message}`);
        return 'failed';
      }
    },
    [churchId, reload, watch, uploads],
  );

  const removeUpload = useCallback(
    async (sourceId: string) => {
      // Client-only placeholders (failed before a record existed) just drop out.
      if (sourceId.startsWith('temp-')) {
        setPreparing((prev) => prev.filter((p) => p.sourceId !== sourceId));
        return;
      }
      if (!churchId) return;
      // Delete the stored audio + stems first (best-effort), then the church record.
      try {
        await chordsApi.deleteSource(sourceId);
      } catch (err) {
        logger.error('Failed to delete chord storage assets:', err);
      }
      await chordUploadsApi.remove(churchId, sourceId);
      await reload();
    },
    [churchId, reload],
  );

  const renameUpload = useCallback(
    async (sourceId: string, title: string) => {
      if (!churchId || sourceId.startsWith('temp-')) return;
      const next = title.trim();
      if (!next) return;
      // Optimistic: update the row immediately, then persist to the church record.
      setUploads((prev) => prev.map((u) => (u.sourceId === sourceId ? { ...u, title: next } : u)));
      try {
        await chordUploadsApi.update(churchId, sourceId, { title: next });
      } catch (err) {
        logger.error('Failed to rename chord upload:', err);
        await reload();
      }
    },
    [churchId, reload],
  );

  // Register any already-pending analyses with the global watcher (e.g. when the
  // page is reopened) so polling + notifications continue across the whole app.
  useEffect(() => {
    if (!churchId) return;
    uploads
      .filter((u) => u.status === 'pending' || u.status === 'processing')
      .forEach((u) => watch(u.sourceId, u.title));
  }, [uploads, churchId, watch]);

  // Refresh the list when the global watcher finalizes an analysis.
  useEffect(() => {
    if (lastCompleted) reload();
  }, [lastCompleted, reload]);

  return {
    uploads: [...preparing, ...uploads],
    loading,
    loadError,
    uploadSong,
    removeUpload,
    renameUpload,
    reload,
  };
}
